import { useMemo } from 'react';
import DOMPurify from 'dompurify';

// Configure DOMPurify once — only allow the tags/attributes this markdown renderer produces.
// Blocks <script>, inline event handlers, javascript: URIs, etc.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'a', 'strong', 'em', 'li', 'br'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  // Forbid all URI schemes except http(s), mailto, tel, and relative URLs
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

const formatInline = (text) =>
  text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(
      /\[(.+?)\]\((.+?)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-teal-600 underline hover:text-teal-700">$1</a>'
    );

const renderLine = (line) => {
  if (line.startsWith('### ')) return `<h3 class="text-xl font-semibold text-slate-900 mt-6 mb-3">${formatInline(line.slice(4))}</h3>`;
  if (line.startsWith('## ')) return `<h2 class="text-2xl font-semibold text-slate-900 mt-8 mb-4">${formatInline(line.slice(3))}</h2>`;
  if (line.startsWith('# ')) return `<h1 class="text-3xl font-bold text-slate-900 mt-8 mb-4">${formatInline(line.slice(2))}</h1>`;
  if (line.startsWith('- ') || line.startsWith('* ')) return `<li class="text-slate-600 leading-relaxed ml-6 list-disc">${formatInline(line.slice(2))}</li>`;
  const numMatch = line.match(/^(\d+)\.\s/);
  if (numMatch) return `<li class="text-slate-600 leading-relaxed ml-6 list-decimal">${formatInline(line.slice(numMatch[0].length))}</li>`;
  if (line.trim() === '') return '<br />';
  return `<p class="text-slate-600 leading-relaxed mb-4">${formatInline(line)}</p>`;
};

// Simple markdown renderer for blog content (sanitized via DOMPurify to prevent XSS).
export const MarkdownContent = ({ content }) => {
  const sanitizedHtml = useMemo(() => {
    if (!content) return '';
    const rawHtml = content.split('\n').map(renderLine).join('');
    return DOMPurify.sanitize(rawHtml, PURIFY_CONFIG);
  }, [content]);

  if (!content) return null;

  // eslint-disable-next-line react/no-danger
  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};
